import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

// ============================================================
// TYPES
// ============================================================

export interface BookingEvent {
  id: string
  title: string
  start: string
  end: string
  backgroundColor: string
  borderColor: string
  textColor: string
  extendedProps: {
    bookingId: string
    projectId: string
    projectName: string
    projectTypeName: string
    color: string
    note: string | null
    createdById: string
    createdByName: string
    participants: Array<{ id: string; name: string | null }>
    hasParticipantOverlap: boolean
  }
}

export interface BookingData {
  id: string
  projectId: string
  startDatetime: string
  endDatetime: string
  note: string | null
  createdById: string
  project: {
    id: string
    name: string
    color: string
    type: { id: string; name: string }
  }
  createdBy: {
    id: string
    name: string
  }
  participants?: Array<{ id: string; name: string | null }>
  hasParticipantOverlap?: boolean
}

interface MonthCounts {
  [date: string]: number
}

interface BookingCalendarState {
  events: BookingEvent[]
  monthCounts: MonthCounts
  loading: boolean
  selectedBooking: BookingData | null
  sidebarOpen: boolean
}

const initialState: BookingCalendarState = {
  events: [],
  monthCounts: {},
  loading: false,
  selectedBooking: null,
  sidebarOpen: false
}

// ============================================================
// HELPERS
// ============================================================

const DEFAULT_PROJECT_COLOR = '#6366F1'

/**
 * Blend a hex color with white so it reads well as a background for dark text.
 * `tint` = share of the original color to keep (0..1). `tint = 0.2` means
 * the result is 20% of the original mixed with 80% white — a pastel shade.
 * Returns '#RRGGBB'. Falls back to the input if it is not a 6-digit hex.
 */
export function lightenForBackground(color: string, tint = 0.2): string {
  const hex = color.trim()
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex)

  if (!match) return color

  const int = parseInt(match[1], 16)
  const r = (int >> 16) & 0xff
  const g = (int >> 8) & 0xff
  const b = int & 0xff
  const mix = (channel: number) => Math.round(channel * tint + 255 * (1 - tint))
  const toHex = (n: number) => n.toString(16).padStart(2, '0')

  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`
}

export function mapBookingToEvent(booking: BookingData): BookingEvent {
  const color = booking.project.color || DEFAULT_PROJECT_COLOR
  const background = lightenForBackground(color)

  return {
    id: booking.id,
    title: booking.project.name,
    start: booking.startDatetime,
    end: booking.endDatetime,
    backgroundColor: background,
    borderColor: color,
    textColor: '#1f2937',
    extendedProps: {
      bookingId: booking.id,
      projectId: booking.projectId,
      projectName: booking.project.name,
      projectTypeName: booking.project.type.name,
      color,
      note: booking.note,
      createdById: booking.createdById,
      createdByName: booking.createdBy.name,
      participants: booking.participants ?? [],
      hasParticipantOverlap: booking.hasParticipantOverlap ?? false
    }
  }
}

// ============================================================
// THUNKS
// ============================================================

export const fetchBookings = createAsyncThunk(
  'bookingCalendar/fetchBookings',
  async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
    const params = new URLSearchParams({ startDate, endDate })
    const res = await fetch(`/api/bookings?${params}`)
    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to fetch bookings')
    }

    return (json.data as BookingData[]).map(mapBookingToEvent)
  }
)

export const fetchMonthCounts = createAsyncThunk(
  'bookingCalendar/fetchMonthCounts',
  async ({ year, month }: { year: number; month: number }) => {
    const params = new URLSearchParams({
      countByDay: 'true',
      year: String(year),
      month: String(month)
    })

    const res = await fetch(`/api/bookings?${params}`)
    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to fetch month counts')
    }

    const counts: MonthCounts = {}

    for (const row of json.data as Array<{ date: string; count: number }>) {
      counts[row.date] = row.count
    }

    return counts
  }
)

export const createBooking = createAsyncThunk(
  'bookingCalendar/createBooking',
  async (data: {
    projectId: string
    startDateTime: string
    endDateTime: string
    note?: string
  }) => {
    const res = await fetch('/api/bookings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to create booking')
    }

    return mapBookingToEvent(json.data as BookingData)
  }
)

export const updateBooking = createAsyncThunk(
  'bookingCalendar/updateBooking',
  async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
    const res = await fetch(`/api/bookings/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to update booking')
    }

    return mapBookingToEvent(json.data as BookingData)
  }
)

export const deleteBooking = createAsyncThunk(
  'bookingCalendar/deleteBooking',
  async (id: string) => {
    const res = await fetch(`/api/bookings/${id}`, {
      method: 'DELETE'
    })

    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Failed to delete booking')
    }

    return id
  }
)

// ============================================================
// SLICE
// ============================================================

export const bookingCalendarSlice = createSlice({
  name: 'bookingCalendar',
  initialState,
  reducers: {
    setSelectedBooking: (state, action: PayloadAction<BookingData | null>) => {
      return { ...state, selectedBooking: action.payload }
    },
    openSidebar: (state) => {
      return { ...state, sidebarOpen: true }
    },
    closeSidebar: (state) => {
      return { ...state, sidebarOpen: false, selectedBooking: null }
    },
    clearMonthCounts: (state) => {
      return { ...state, monthCounts: {} }
    }
  },
  extraReducers: (builder) => {
    // fetchBookings
    builder.addCase(fetchBookings.pending, (state) => {
      return { ...state, loading: true }
    })

    builder.addCase(fetchBookings.fulfilled, (state, action) => {
      return { ...state, events: action.payload, loading: false }
    })

    builder.addCase(fetchBookings.rejected, (state) => {
      return { ...state, loading: false }
    })

    // fetchMonthCounts
    builder.addCase(fetchMonthCounts.fulfilled, (state, action) => {
      return { ...state, monthCounts: action.payload }
    })

    // createBooking
    builder.addCase(createBooking.fulfilled, (state, action) => {
      return {
        ...state,
        events: [...state.events, action.payload],
        sidebarOpen: false
      }
    })

    // updateBooking
    builder.addCase(updateBooking.fulfilled, (state, action) => {
      const updatedEvent = action.payload
      const updatedEvents = state.events.map((evt) =>
        evt.id === updatedEvent.id ? updatedEvent : evt
      )

      return { ...state, events: updatedEvents, selectedBooking: null }
    })

    // deleteBooking
    builder.addCase(deleteBooking.fulfilled, (state, action) => {
      const deletedId = action.payload
      const filteredEvents = state.events.filter((evt) => evt.id !== deletedId)

      return { ...state, events: filteredEvents, selectedBooking: null }
    })
  }
})

export const { setSelectedBooking, openSidebar, closeSidebar, clearMonthCounts } = bookingCalendarSlice.actions

export default bookingCalendarSlice.reducer
